import { useState, type Dispatch, type SetStateAction } from "react";
import { useCustom } from "@refinedev/core";
import { CalendarDays, Gift, Info, Minus, PawPrint, Plus, ShoppingCart, Trash2, UserRound } from "lucide-react";

import { useAnimalPhotoUrls } from "../../api/useAnimalPhotoUrls";
import { formatEuro } from "../../i18n/format";
import type { PensionDier } from "../pensionKlantTypes";
import { dagenTussen, geslachtTeken, leeftijd, type Context } from "../pensionklant/hulp";
import { eenheden, prijsTekst, type Arrangement } from "../pensionarrangement/arrangement";
import { KlantBalk, Terug, Voet, Volgende, isDatum, useKlantDieren, type GekozenKlant, type Periode, type Vertaal } from "./bouwstenen";
import { EigenElementen, GEEN_ANTWOORDEN, NIETS, VerplichtMelding, useVerplicht } from "./EigenElementen";
import { rondBlok, stapKop, type Antwoorden, type WizardStap } from "./indeling";

/** Per dier ("dier:12") en voor de hele reservering ("reservering"): per arrangement het aantal. */
export type Extras = Record<string, Record<number, number>>;

export const RESERVERING = "reservering";
export const dierSleutel = (id: number) => `dier:${id}`;

/** Het adres van de arrangementen die bij deze periode gelden; de wizard en de stap delen zo één opvraging. */
export const arrangementenUrl = (aankomst: string) =>
  `pension/arrangementen?actief=true${isDatum(aankomst) ? `&peildatum=${aankomst}` : ""}`;

/**
 * Of er voor deze dieren of voor de reservering iets te kiezen is. Is dat niet zo, dan slaat de wizard de stap over
 * (Maarten, 2026-10-14: "als er geen arrangementen zijn dan hoef je ze ook niet te laten zien").
 */
export function heeftArrangementen(alle: Arrangement[], dieren: PensionDier[]): boolean {
  return alle.some((a) => past(a, undefined) || dieren.some((d) => past(a, d)));
}

/** Wat een regel kost: de prijs maal per keer het aantal, per dag de dagen, per week de begonnen weken. */
export function regelBedrag(a: Pick<Arrangement, "prijs" | "eenheid">, aantal: number, dagen: number): number {
  return a.prijs ? a.prijs.bedrag * eenheden(a.eenheid, aantal, dagen) : 0;
}

export function past(a: Arrangement, dier: PensionDier | undefined): boolean {
  if (!dier) return a.niveau === "RESERVERING";
  return a.niveau === "DIER" && (a.diersoortId == null || (a.diersoort ?? "").toLowerCase() === (dier.diersoort ?? "").toLowerCase());
}

/**
 * Stap Arrangementen &amp; extra's: per dier en voor de hele reservering de arrangementen van Producten › Pension
 * arrangementen, met rechts wat er gekozen is en wat het kost. De stap is optioneel.
 */
export function ArrangementenStap({
  klant,
  dierIds,
  periode,
  extras,
  setExtras,
  ctx,
  onKlant,
  onTerug,
  onVolgende,
  stap,
  antwoorden = GEEN_ANTWOORDEN,
  setAntwoorden = NIETS,
}: {
  stap?: WizardStap;
  antwoorden?: Antwoorden;
  setAntwoorden?: Dispatch<SetStateAction<Antwoorden>>;
  klant: GekozenKlant;
  dierIds: number[];
  periode: Periode;
  extras: Extras;
  setExtras: Dispatch<SetStateAction<Extras>>;
  ctx: Context;
  onKlant: () => void;
  onTerug: () => void;
  onVolgende: () => void;
}) {
  const t = ctx.t as Vertaal;
  const { locale } = ctx;
  const { dieren } = useKlantDieren(klant.id);
  const gekozen = dieren.filter((d) => dierIds.includes(d.id));
  const fotos = useAnimalPhotoUrls(gekozen.map((d) => d.id));
  const lijst = useCustom<Arrangement[]>({ url: arrangementenUrl(periode.aankomst), method: "get" });
  const alle = Array.isArray(lijst.data?.data) ? lijst.data.data : [];
  const perId = new Map(alle.map((a) => [a.id, a]));
  // Alleen tabbladen waar iets te kiezen is: een dier zonder passende arrangementen krijgt er geen, en "Reservering"
  // alleen als er arrangementen voor de hele reservering zijn.
  const metKeuze = gekozen.filter((d) => alle.some((a) => past(a, d)));
  const reserveringKan = alle.some((a) => past(a, undefined));
  const tabs = [...metKeuze.map((d) => dierSleutel(d.id)), ...(reserveringKan ? [RESERVERING] : [])];
  const [tab, setTab] = useState<string | null>(null);
  const actief = tab != null && tabs.includes(tab) ? tab : (tabs[0] ?? RESERVERING);
  const dier = gekozen.find((d) => dierSleutel(d.id) === actief);
  const dagen = Math.max(1, dagenTussen(periode.aankomst, periode.vertrek));
  const kop = stapKop(t, stap, "arrangementen");
  const verplicht = useVerplicht(stap, antwoorden, dierIds);
  const rond = rondBlok(stap, "ARRANGEMENTEN");

  const zet = (sleutel: string, id: number, aantal: number) =>
    setExtras((x) => {
      const deel = { ...(x[sleutel] ?? {}) };
      if (aantal <= 0) delete deel[id];
      else deel[id] = aantal;
      return { ...x, [sleutel]: deel };
    });

  const secties = [
    ...gekozen.map((d) => ({ sleutel: dierSleutel(d.id), naam: d.naam ?? String(d.id), dier: d as PensionDier | undefined })),
    { sleutel: RESERVERING, naam: t("nieuweReservering.arrangementen.reservering"), dier: undefined },
  ].map((s) => ({
    ...s,
    regels: Object.entries(extras[s.sleutel] ?? {})
      .map(([id, aantal]) => ({ arrangement: perId.get(Number(id)), aantal }))
      .filter((r): r is { arrangement: Arrangement; aantal: number } => r.arrangement != null),
  }));
  const totaal = secties.reduce((som, s) => som + s.regels.reduce((x, r) => x + regelBedrag(r.arrangement, r.aantal, dagen), 0), 0);
  const aantalGekozen = (sleutel: string) => Object.keys(extras[sleutel] ?? {}).length;
  const zichtbaar = alle.filter((a) => past(a, dier));
  const foto = (d: PensionDier) =>
    fotos[String(d.id)] ? (
      <img className="nr-extra__mini" src={fotos[String(d.id)]} alt="" />
    ) : (
      <span className="nr-extra__mini nr-dier__foto--leeg" aria-hidden="true">
        <PawPrint size={18} />
      </span>
    );

  return (
    <>
      <div className="nr__inhoud">
        <KlantBalk
          klant={klant}
          knop={t("nieuweReservering.periode.klantBekijken")}
          knopIcoon={<UserRound size={16} aria-hidden="true" />}
          onKnop={onKlant}
        />
        <h3 className="nr-kaart__titel">{kop.titel}</h3>
        <p className="nr-kaart__uitleg">{kop.uitleg}</p>
        <EigenElementen
          elementen={rond.voor}
          dieren={gekozen}
          antwoorden={antwoorden}
          setAntwoorden={setAntwoorden}
          t={t}
          geprobeerd={verplicht.geprobeerd}
        />

        {/* Niets te kiezen (de stap staat er dan alleen voor eigen vragen): geen lege tabbladen. */}
        {(lijst.isLoading || lijst.isError || tabs.length > 0) && (
        <div className="nr-extra">
          <div className="nr-extra__links">
            <div className="nr-extra__tabs" role="tablist" aria-label={t("nieuweReservering.arrangementen.voorWie")}>
              {metKeuze.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  role="tab"
                  aria-selected={actief === dierSleutel(d.id)}
                  className="nr-extra__tab"
                  onClick={() => setTab(dierSleutel(d.id))}
                >
                  {foto(d)}
                  <span className="nr-extra__tabtekst">
                    <strong>{d.naam ?? "—"}</strong>
                    <span>{d.diersoort}</span>
                  </span>
                  {aantalGekozen(dierSleutel(d.id)) > 0 && <span className="nr-extra__teller">{aantalGekozen(dierSleutel(d.id))}</span>}
                </button>
              ))}
              {reserveringKan && (
              <button
                type="button"
                role="tab"
                aria-selected={actief === RESERVERING}
                className="nr-extra__tab"
                onClick={() => setTab(RESERVERING)}
              >
                <span className="nr-rond" aria-hidden="true">
                  <CalendarDays size={20} />
                </span>
                <span className="nr-extra__tabtekst">
                  <strong>{t("nieuweReservering.arrangementen.reservering")}</strong>
                  <span>{t("nieuweReservering.arrangementen.reserveringUitleg")}</span>
                </span>
                {aantalGekozen(RESERVERING) > 0 && <span className="nr-extra__teller">{aantalGekozen(RESERVERING)}</span>}
              </button>
              )}
            </div>

            {dier && (
              <div className="nr-extra__dier">
                {foto(dier)}
                <span className="nr-dier__tekst">
                  <strong className="nr-dier__naam">{dier.naam}</strong>
                  <span className="nr-dier__soort">
                    {[dier.diersoort, dier.ras, [geslachtTeken(dier.geslacht), dier.geslacht].filter(Boolean).join(" "), leeftijd(dier.geboortedatum, ctx)]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
              </div>
            )}

            <div className="nr-extra__raster" role="tabpanel" aria-label={dier ? (dier.naam ?? "") : t("nieuweReservering.arrangementen.reservering")}>
              {lijst.isLoading && <p className="nr-kaart__uitleg">{t("common.loading")}</p>}
              {lijst.isError && (
                <p className="nr-melding" role="alert">
                  {t("nieuweReservering.arrangementen.laadFout")}
                </p>
              )}
              {!lijst.isLoading && !lijst.isError && zichtbaar.length === 0 && (
                <p className="nr-kaart__uitleg">{t("nieuweReservering.arrangementen.geen")}</p>
              )}
              {zichtbaar.map((a) => {
                const aantal = extras[actief]?.[a.id] ?? 0;
                return (
                  <label key={a.id} className="nr-extra__kaart" data-aan={aantal > 0 || undefined}>
                    <input
                      type="checkbox"
                      className="nr-extra__vink"
                      checked={aantal > 0}
                      aria-label={a.naam}
                      onChange={(e) => zet(actief, a.id, e.target.checked ? 1 : 0)}
                    />
                    {a.foto ? (
                      <img className="nr-extra__foto" src={a.foto} alt="" />
                    ) : (
                      <span className="nr-extra__foto nr-extra__foto--leeg" aria-hidden="true">
                        <Gift size={28} />
                      </span>
                    )}
                    <strong>{a.naam}</strong>
                    {a.omschrijving && <span className="nr-klein">{a.omschrijving}</span>}
                    <span className="nr-extra__prijs">{prijsTekst(a, t, locale)}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <aside className="nr-extra__overzicht" aria-label={t("nieuweReservering.arrangementen.gekozen")}>
            <div className="nr-paneel__kop">
              <span className="nr-rond" aria-hidden="true">
                <ShoppingCart size={20} />
              </span>
              <div>
                <h4>{t("nieuweReservering.arrangementen.gekozen")}</h4>
                <p>{t("nieuweReservering.arrangementen.gekozenUitleg")}</p>
              </div>
            </div>
            {secties
              .filter((s) => s.regels.length > 0)
              .map((s) => (
                <section key={s.sleutel} className="nr-extra__sectie" aria-label={s.naam}>
                  <h5>
                    {s.dier ? (
                      foto(s.dier)
                    ) : (
                      <span className="nr-rond" aria-hidden="true">
                        <CalendarDays size={18} />
                      </span>
                    )}
                    {s.naam}
                  </h5>
                  <ul>
                    {s.regels.map(({ arrangement: a, aantal }) => (
                      <li key={a.id} className="nr-extra__regel">
                        <span className="nr-aantal">
                          <button
                            type="button"
                            aria-label={t("nieuweReservering.arrangementen.minder", { args: [a.naam] })}
                            onClick={() => zet(s.sleutel, a.id, aantal - 1)}
                          >
                            <Minus size={13} aria-hidden="true" />
                          </button>
                          <span>{aantal}×</span>
                          <button
                            type="button"
                            disabled={a.maxAantal != null && aantal >= a.maxAantal}
                            aria-label={t("nieuweReservering.arrangementen.meer", { args: [a.naam] })}
                            onClick={() => zet(s.sleutel, a.id, aantal + 1)}
                          >
                            <Plus size={13} aria-hidden="true" />
                          </button>
                        </span>
                        <span className="nr-extra__regelnaam">{a.naam}</span>
                        <span className="nr-extra__bedrag">{a.prijs ? formatEuro(regelBedrag(a, aantal, dagen), locale) : "—"}</span>
                        <button
                          type="button"
                          className="nr-extra__weg"
                          aria-label={t("nieuweReservering.arrangementen.weg", { args: [a.naam] })}
                          onClick={() => zet(s.sleutel, a.id, 0)}
                        >
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            {secties.every((s) => s.regels.length === 0) && <p className="nr-klein">{t("nieuweReservering.arrangementen.nogNiets")}</p>}
            <div className="nr-extra__totaal">
              <span>{t("nieuweReservering.arrangementen.totaal")}</span>
              <strong>{formatEuro(totaal, locale)}</strong>
            </div>
            <div className="nr-ai">
              <Info size={20} aria-hidden="true" />
              <div>
                <strong>{t("nieuweReservering.arrangementen.overslaanTitel")}</strong>
                {t("nieuweReservering.arrangementen.overslaanUitleg")}
              </div>
            </div>
          </aside>
        </div>
        )}
        <EigenElementen
          elementen={rond.na}
          dieren={gekozen}
          antwoorden={antwoorden}
          setAntwoorden={setAntwoorden}
          t={t}
          geprobeerd={verplicht.geprobeerd}
        />
        <VerplichtMelding open={verplicht.open} geprobeerd={verplicht.geprobeerd} t={t} />
      </div>
      <Voet
        links={<Terug t={t} onClick={onTerug} label={stap ? undefined : t("nieuweReservering.arrangementen.terug")} />}
        rechts={
          <>
            <button type="button" className="nr-knop nr-knop--link" onClick={verplicht.verder(onVolgende)}>
              {t("nieuweReservering.arrangementen.overslaan")}
            </button>
            <Volgende t={t} onClick={verplicht.verder(onVolgende)} />
          </>
        }
      />
    </>
  );
}
